import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export interface ListingStepPayload {
  id?: string; // present from step 2 onwards
  step: number; // 1-based step number
  mode: 'create' | 'update' | 'submit';
  data: Record<string, any>; // step-specific fields
}

export interface ListingResponse {
  success: boolean;
  message?: string;
  id?: string; // listing id returned from backend
  nextStep?: number; // backend can tell which step to go next
  data?: Record<string, any>; // hydrated data
}

export const listingsService = {
  upsertStep: async (payload: ListingStepPayload): Promise<ListingResponse> => {
    // POST for create (no id), PUT for update (has id)
    const hasId = Boolean(payload.id);
    const url = hasId
      ? `${API_ENDPOINTS.LISTINGS.CREATE_OR_UPDATE_STEP}/${payload.id}`
      : API_ENDPOINTS.LISTINGS.CREATE_OR_UPDATE_STEP;
    // Backend expects PATCH for updates
    const method = hasId ? 'patch' : 'post';
    const { data } = await axiosInstance.request<ListingResponse>({
      url,
      method: method as 'post' | 'patch',
      data: payload,
    });
    return data;
  },

  submit: async (payload: ListingStepPayload): Promise<ListingResponse> => {
    const { data } = await axiosInstance.post<ListingResponse>(
      API_ENDPOINTS.LISTINGS.SUBMIT,
      payload
    );
    return data;
  },

  publishWithCredits: async (id: string): Promise<{ listing: any; creditBalance: number; message: string }> => {
    const { data } = await axiosInstance.post(
      API_ENDPOINTS.LISTINGS.PUBLISH(id)
    );
    return data;
  },

  uploadPhotos: async (
    id: string,
    files: File[]
  ): Promise<{ urls: string[]; count: number; message?: string }> => {
    // Attempt 1: repeated 'files' keys (e.g., Multer array('files'))
    const formFiles = new FormData();
    files.forEach(f => formFiles.append('files', f, f.name));
    try {
      const { data } = await axiosInstance.post(
        API_ENDPOINTS.LISTINGS.PHOTOS_UPLOAD(id),
        formFiles,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return data;
    } catch (err: any) {
      // Attempt 2: repeated 'files[]' keys (some backends expect array syntax)
      const status = err?.status || err?.response?.status;
      if (status === 400) {
        const formFilesArraySyntax = new FormData();
        files.forEach(f => formFilesArraySyntax.append('files[]', f, f.name));
        try {
          const { data } = await axiosInstance.post(
            API_ENDPOINTS.LISTINGS.PHOTOS_UPLOAD(id),
            formFilesArraySyntax,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          return data;
        } catch (err2: any) {
          // Attempt 3: singular 'file' (first only) — common single-file handler
          const formSingle = new FormData();
          if (files[0]) formSingle.append('file', files[0], files[0].name);
          const { data } = await axiosInstance.post(
            API_ENDPOINTS.LISTINGS.PHOTOS_UPLOAD(id),
            formSingle,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          return data;
        }
      }
      throw err;
    }
  },

  getById: async (id: string): Promise<ListingResponse> => {
    const { data } = await axiosInstance.get<ListingResponse>(
      API_ENDPOINTS.LISTINGS.GET_BY_ID(id)
    );
    return data;
  },

  delete: async (id: string): Promise<void> => {
    const { data } = await axiosInstance.delete(
      API_ENDPOINTS.LISTINGS.DELETE(id)
    );
    return data;
  },

  getAll: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    listings: any[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> => {
    const { data } = await axiosInstance.get('/listings', { params });
    return data;
  },

  getSimilar: async (id: string, limit = 6): Promise<any[]> => {
    const { data } = await axiosInstance.get(`/listings/${id}/similar`, { params: { limit } });
    return data?.data ?? [];
  },

  getContactInfo: async (id: string): Promise<{ phone: string | null; whatsapp: string | null }> => {
    const { data } = await axiosInstance.get(`/listings/${id}/contact`);
    return data?.data ?? { phone: null, whatsapp: null };
  },
};

export default listingsService;
